import { type PropsWithChildren, useMemo } from 'react';

import { Button, type ButtonProps } from '@backyard-ui/core';

import { type Seller } from '@/infra/services/catalog/product/get-sellers/model';
import { type UseAddProductsToCartProps } from '@/presenters/hooks/checkout/cart/use-add-products-to-cart/use-add-products-to-cart';

import useActions from './useActions';
import { VoltageDialog } from './VoltageDialog';

interface BaseButtonProps
  extends Pick<ButtonProps, 'size' | 'variant'>,
    PropsWithChildren,
    Pick<UseAddProductsToCartProps, 'onError' | 'onSuccess' | 'postSuccess'> {
  seller?: Seller;
  quantity: number;
  dataGtmElement?: string;
}

export default function BaseButton(props: Readonly<BaseButtonProps>) {
  const {
    size,
    variant,
    onError,
    onSuccess,
    seller,
    children,
    quantity,
    dataGtmElement,
    postSuccess,
    ...rest
  } = props;

  const {
    isDisabled,
    onClick,
    onConfirm,
    voltageDialogIsOpen,
    setVoltageDialogIsOpen,
    isLoading,
    shouldShowVoltageModal,
  } = useActions({
    onError,
    onSuccess,
    seller,
    quantity,
    postSuccess,
  });

  const button = useMemo(
    () => (
      <Button
        UNSAFE_className="whitespace-nowrap"
        disabled={isDisabled}
        isLoading={isLoading}
        onClick={onClick}
        variant={variant}
        size={size}
        isFullWidth
        data-gtm-element={dataGtmElement}
        {...rest}
      >
        {children}
      </Button>
    ),
    [
      isDisabled,
      isLoading,
      onClick,
      variant,
      size,
      dataGtmElement,
      children,
      rest,
    ]
  );

  if (shouldShowVoltageModal) {
    return (
      <VoltageDialog
        isOpen={voltageDialogIsOpen}
        onConfirm={onConfirm}
        onOpenChange={(value) => setVoltageDialogIsOpen(value)}
      >
        {button}
      </VoltageDialog>
    );
  }

  return button;
}
